import { Component, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDrawer } from '@angular/material/sidenav';

import { CerPromptService } from '@cer/cer-ui/cer-ui-prompt.service';
import { CerRouteDataInfo } from '@app/app.module-generated';

import { CerAppLayoutHelpAboutComponent } from './cer-app-layout-help-about/cer-app-layout-help-about.component';
import { CerAppRouteService } from '../cer-app-route/cer-app-route.service';
import { CerSessionService } from '@cer/cer-session/cer-session.service';
import { RoleTypeEnum } from '@app/api';
import { CerAppThemeService, CerColorTheme } from '../cer-app-theme/cer-app-theme.service';

@Component({
  selector: 'cer-app-layout',
  templateUrl: './cer-app-layout.component.html',
  styleUrls: ['cer-app-layout.component.css']
})
export class CerAppLayoutComponent implements OnDestroy {
  private subscriptionManager$: Subscription = new Subscription();

  @ViewChild('drawer') drawer: MatDrawer;

  protected appTitle$: BehaviorSubject<string>;
  protected toolbarStyle$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  protected activeThemeIcon$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  protected isHelpActive: boolean = false;

  public isHandset$: Observable<boolean> = this.breakpointObserver.observe('(max-width: 932.00px)')
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  constructor(private breakpointObserver: BreakpointObserver, private routeService: CerAppRouteService,
    private themeService: CerAppThemeService, private session: CerSessionService, private ui: CerPromptService) {
    this.appTitle$ = session.environment.title$;

    this.subscriptionManager$.add(
      this.themeService.activeThemeIcon$.subscribe(icon => this.activeThemeIcon$.next(icon))
    );
    this.subscriptionManager$.add(
      session.environment.primaryColor$.subscribe(c => this.toolbarStyle$.next('backgroundColor: ' + c + ';'))
    );
    this.subscriptionManager$.add(
      this.session.environment.clientNeedsReload$.subscribe(reload => {
        if (reload === true) {
          this.showAppHelpAbout(reload).then(ok => this.session.environment.clientNeedsReload$.next(false))
        }
      }));
    this.subscriptionManager$.add(
      this.routeService.routeDataInfo$.subscribe(routeDataInfo => this.onRouteDataInfo(routeDataInfo))
    );
    this.subscriptionManager$.add(
      this.routeService.drawerClose$.subscribe(close => this.drawerClose(close))
    );
    this.subscriptionManager$.add(
      combineLatest([this.session.isAuthenticated$, this.session.user.roles$]).subscribe(([isAuthenticated, userRoles]) => {
        const hasHelpAccess = userRoles.find(r => r.roleType == RoleTypeEnum.DocumentationView) != null;
        this.isHelpActive = isAuthenticated && hasHelpAccess;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptionManager$.unsubscribe();
  }

  ngAfterViewInit() {
    var alerts = (document.querySelector('.app-alerts') as HTMLElement);
    if (alerts) {
      alerts.style.top = '150px';
    }
  }

  public onRouterOutletActivate(component: any) {
  }

  public onRouterOutletDeactivate(component: any) {
    // you have access to the component instance
  }

  private onRouteDataInfo(routeDataInfo: CerRouteDataInfo) {
    var drawer = this.drawer;
    const url = routeDataInfo?.url;
    if (url?.length > 0 && drawer?.mode == 'over' && drawer.opened) {
      drawer.close();
    }
  }

  private drawerClose(close: boolean) {
    if (close) {
      this.drawer.close();
    }
    else {
      this.drawer.open();
    }
  }


  protected selectTheme(theme: CerColorTheme) {
    this.themeService.selectTheme(theme);
  }

  public showHelp() {
    this.routeService.redirectToUrl("/help");
  }

  public async showAppHelpAbout(askClientUpdate: boolean = false): Promise<boolean> {
    this.ui.showDialogComponent(CerAppLayoutHelpAboutComponent, { width: '350px', height: 'auto', panelClass: 'custom-mat-dialog-panel', data: { askClientUpdate: askClientUpdate } });
    return true;
  }
}
